import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { History, FileDown, Pencil, Trash2, Loader2, Upload } from "lucide-react";

const money = (v) => (v || v === 0 ? `£${Number(v).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—");

// Client drawer: record that the client is already engaged under a Bright Manager LoE.
export function LegacyEngagement({ clientId }) {
  const [rec, setRec] = useState(undefined);
  const [editing, setEditing] = useState(false);
  const load = () => api.get(`/clients/${clientId}/legacy-engagement`).then((r) => setRec(r.data)).catch(() => setRec(null));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId]);

  async function download() {
    try {
      const r = await api.get(`/clients/${clientId}/legacy-engagement/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = rec.file_name || "legacy_loe.pdf"; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { toast.error("Could not download the PDF"); }
  }
  async function remove() {
    if (!window.confirm("Remove the legacy engagement record for this client?")) return;
    await api.delete(`/clients/${clientId}/legacy-engagement`);
    toast.success("Legacy engagement removed");
    setRec(null);
  }

  if (rec === undefined) return null;
  if (editing) {
    return <LegacyForm clientId={clientId} rec={rec} onCancel={() => setEditing(false)} onSaved={(d) => { setRec(d); setEditing(false); }} />;
  }
  return (
    <div className="card-flat p-4 mb-4" data-testid="legacy-engagement">
      <div className="flex items-center justify-between mb-1">
        <div className="overline flex items-center gap-1.5"><History size={12} /> Existing engagement (Bright Manager)</div>
        {rec && (
          <div className="flex items-center gap-1">
            <button type="button" data-testid="legacy-edit" onClick={() => setEditing(true)} className="p-1 text-[var(--ink-2)] hover:text-[var(--navy)]" title="Edit"><Pencil size={12} /></button>
            <button type="button" data-testid="legacy-remove" onClick={remove} className="p-1 text-[var(--ink-2)] hover:text-[var(--err)]" title="Remove"><Trash2 size={12} /></button>
          </div>
        )}
      </div>
      {rec ? (
        <div className="text-sm space-y-1.5" data-testid="legacy-summary">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border border-[var(--ok)] text-[var(--ok)] bg-[var(--ok-bg)]">Engaged · Legacy</span>
            <span className="text-xs">LoE signed <b data-testid="legacy-signed-date">{new Date(rec.signed_date).toLocaleDateString("en-GB", { dateStyle: "medium" })}</b></span>
            <span className="text-xs font-mono-data">{money(rec.annual_fee)} / yr · {money(rec.monthly_fee)} / mo</span>
          </div>
          {rec.services?.length > 0 && (
            <div className="flex flex-wrap gap-1">{rec.services.map((s) => <span key={s} className="text-[10px] px-1.5 py-px rounded-sm bg-[var(--bg-2)] border border-[var(--line)]">{s}</span>)}</div>
          )}
          {rec.notes && <div className="text-xs text-[var(--ink-2)]">{rec.notes}</div>}
          <div className="flex items-center justify-between pt-1">
            <span className="text-[10px] text-[var(--ink-2)]">Recorded by {rec.updated_by}</span>
            {rec.file_name ? (
              <button type="button" data-testid="legacy-download" onClick={download} className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-[var(--navy)] text-[var(--navy)] hover:bg-[var(--navy)]/5">
                <FileDown size={9} /> Signed LoE PDF
              </button>
            ) : <span className="text-[10px] text-[var(--ink-2)]">No PDF attached</span>}
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-3">
          <div className="text-xs text-[var(--ink-2)]">Already engaged under a Bright Manager LoE? Record it here — no re-signing needed.</div>
          <button type="button" data-testid="legacy-mark-btn" onClick={() => setEditing(true)} className="btn-ghost text-xs !py-1.5 !px-2.5 whitespace-nowrap">Mark as engaged (legacy)</button>
        </div>
      )}
    </div>
  );
}

function LegacyForm({ clientId, rec, onCancel, onSaved }) {
  const [signedDate, setSignedDate] = useState(rec?.signed_date || "");
  const [annual, setAnnual] = useState(rec?.annual_fee ?? "");
  const [monthly, setMonthly] = useState(rec?.monthly_fee ?? "");
  const [services, setServices] = useState(new Set(rec?.services || []));
  const [catalogue, setCatalogue] = useState([]);
  const [notes, setNotes] = useState(rec?.notes || "");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const round = (v) => Math.round(v * 100) / 100;

  useEffect(() => { api.get("/onboarding/available-services").then((r) => setCatalogue(r.data.services || [])).catch(() => {}); }, []);

  function toggle(label) {
    const n = new Set(services);
    n.has(label) ? n.delete(label) : n.add(label);
    setServices(n);
  }

  async function save() {
    if (!signedDate) { toast.error("Enter the date the LoE was signed"); return; }
    if (file && !file.name.toLowerCase().endsWith(".pdf")) { toast.error("Please attach a PDF"); return; }
    const fd = new FormData();
    fd.append("signed_date", signedDate);
    if (annual !== "") fd.append("annual_fee", annual);
    if (monthly !== "") fd.append("monthly_fee", monthly);
    fd.append("services", [...services].join("|"));
    fd.append("notes", notes);
    if (file) fd.append("file", file);
    setBusy(true);
    try {
      const r = await api.post(`/clients/${clientId}/legacy-engagement`, fd);
      toast.success("Legacy engagement saved");
      onSaved(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  }

  const lbl = "text-[10px] uppercase tracking-wider text-[var(--ink-2)] block mb-0.5";
  return (
    <div className="card-flat p-4 mb-4" data-testid="legacy-form">
      <div className="overline flex items-center gap-1.5 mb-3"><History size={12} /> {rec ? "Edit" : "Mark as engaged"} — legacy Bright Manager LoE</div>
      <div className="grid grid-cols-3 gap-3 mb-3">
        <div>
          <label className={lbl}>LoE signed date *</label>
          <input data-testid="legacy-date" type="date" value={signedDate} onChange={(e) => setSignedDate(e.target.value)} className="input-slim w-full text-xs" />
        </div>
        <div>
          <label className={lbl}>Annual fee (£)</label>
          <input data-testid="legacy-annual" type="number" min="0" step="0.01" value={annual}
            onChange={(e) => { setAnnual(e.target.value); setMonthly(e.target.value === "" ? "" : round(Number(e.target.value) / 12)); }} className="input-slim w-full text-xs text-right" />
        </div>
        <div>
          <label className={lbl}>Monthly fee (£)</label>
          <input data-testid="legacy-monthly" type="number" min="0" step="0.01" value={monthly}
            onChange={(e) => { setMonthly(e.target.value); setAnnual(e.target.value === "" ? "" : round(Number(e.target.value) * 12)); }} className="input-slim w-full text-xs text-right" />
        </div>
      </div>
      {catalogue.length > 0 && (
        <div className="mb-3">
          <label className={lbl}>Services covered</label>
          <div className="flex flex-wrap gap-1" data-testid="legacy-services">
            {catalogue.map((s) => {
              const on = services.has(s.label);
              return (
                <button key={s.key} type="button" data-testid={`legacy-svc-${s.key}`} onClick={() => toggle(s.label)}
                  className={`text-[10px] px-1.5 py-0.5 rounded-sm border transition-colors ${on ? "border-[var(--navy)] bg-[var(--navy)] text-white" : "border-[var(--line)] text-[var(--ink-2)] hover:border-[var(--navy)]"}`}>
                  {s.label}
                </button>
              );
            })}
          </div>
        </div>
      )}
      <div className="mb-3">
        <label className={lbl}>Notes</label>
        <input data-testid="legacy-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. LoE v3 2019, fee uplift Apr 2023" className="input-slim w-full text-xs" />
      </div>
      <div className="mb-3">
        <label className={lbl}>Signed LoE PDF {rec?.file_name ? `(current: ${rec.file_name})` : "(optional)"}</label>
        <label className="flex items-center gap-2 text-xs border border-dashed border-[var(--line)] rounded-sm px-2 py-1.5 cursor-pointer hover:border-[var(--navy)]">
          <Upload size={12} /> <span className="truncate">{file ? file.name : rec?.file_name ? "Replace PDF…" : "Choose PDF…"}</span>
          <input data-testid="legacy-file" type="file" accept="application/pdf,.pdf" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
        </label>
      </div>
      <div className="flex justify-end gap-2">
        <button type="button" data-testid="legacy-cancel" onClick={onCancel} className="btn-ghost text-xs !py-1.5 !px-2.5">Cancel</button>
        <button type="button" data-testid="legacy-save" onClick={save} disabled={busy} className="btn-primary flex items-center gap-1.5 text-xs !py-1.5 !px-2.5">
          {busy && <Loader2 size={12} className="animate-spin" />} Save
        </button>
      </div>
    </div>
  );
}
